/**
 * Phone Verification via Firebase Phone Auth (reCAPTCHA + SMS OTP).
 * Reads Firebase config from #firebase-config data attributes, initializes Firebase,
 * and exposes window.PhoneVerification for use by phone-verification-modal.js.
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('firebase-config');
  if (cfgEl && typeof firebase !== 'undefined' && !firebase.apps.length) {
    firebase.initializeApp({
      apiKey: cfgEl.getAttribute('data-api-key') || '',
      authDomain: cfgEl.getAttribute('data-auth-domain') || '',
      projectId: cfgEl.getAttribute('data-project-id') || '',
      storageBucket: cfgEl.getAttribute('data-storage-bucket') || '',
      messagingSenderId: cfgEl.getAttribute('data-messaging-sender-id') || '',
      appId: cfgEl.getAttribute('data-app-id') || ''
    });
  }

  var recaptchaVerifier = null;
  var confirmationResult = null;
  var lastPhoneE164 = '';

  /**
   * Fire-and-forget audit log for every Firebase phone-verification stage.
   * Surfaces in /admin/sms-logs so support can answer "did the user even
   * press send code, and what did Firebase actually say?" — same coverage
   * the mobile app already has (see phone_verification_page.dart).
   *
   * Uses keepalive so events still flush if the user navigates away mid-
   * flow (e.g. after `code_sent` they switch to their SMS app to copy the
   * code, then come back and reload).
   */
  function logStage(stage, errorCode, errorMessage) {
    try {
      if (!stage || !lastPhoneE164) return;
      var csrf = window.CSRFHelper && window.CSRFHelper.getCSRFData
        ? window.CSRFHelper.getCSRFData()
        : { name: 'carbuy_csrf', value: '' };
      var fd = new FormData();
      fd.append('phone', lastPhoneE164);
      fd.append('stage', stage);
      if (errorCode) fd.append('error_code', String(errorCode).substring(0, 64));
      if (errorMessage) fd.append('error_message', String(errorMessage).substring(0, 500));
      if (csrf && csrf.name) fd.append(csrf.name, csrf.value || '');

      fetch('/profile/phone-verification/log', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (data) {
          if (data && data.csrf_hash && window.CSRFHelper && window.CSRFHelper.updateCSRFMeta) {
            window.CSRFHelper.updateCSRFMeta(data);
          }
        })
        .catch(function () { /* best-effort */ });
    } catch (_e) { /* never break the verification flow over a log call */ }
  }

  window.PhoneVerification = {
    /**
     * Initialize reCAPTCHA and send SMS code.
     * @param {string} phoneNumber - E.164 formatted phone number
     * @param {function} onCodeSent - Called when SMS is sent
     * @param {function} onError - Called on error with message string
     */
    sendCode: function (phoneNumber, onCodeSent, onError) {
      lastPhoneE164 = phoneNumber || '';

      if (typeof firebase === 'undefined' || !firebase.auth) {
        logStage('verification_failed', 'firebase_not_loaded', 'Firebase SDK not loaded on the page.');
        onError('Firebase not loaded');
        return;
      }

      // Tear down any previous verifier+widget BEFORE we recreate. Just calling
      // verifier.clear() is not enough on the Firebase compat SDK — the DOM
      // node still has the rendered iframe, and `new RecaptchaVerifier(...)`
      // on the same element throws "reCAPTCHA has already been rendered in
      // this element". That error stuck users in a Send → error → Send loop
      // (see /admin/sms-logs entries 2026-04-23 23:35:20+) until they hard-
      // reloaded. The reliable fix is to drop the container element entirely
      // and recreate it, and to null out our reference so any future SDK call
      // can't reach into a half-disposed verifier.
      var containerId = 'phone-recaptcha-container';
      if (recaptchaVerifier) {
        try { recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
        recaptchaVerifier = null;
      }
      var oldContainer = document.getElementById(containerId);
      if (oldContainer && oldContainer.parentNode) {
        oldContainer.parentNode.removeChild(oldContainer);
      }
      var container = document.createElement('div');
      container.id = containerId;
      container.style.display = 'none';
      document.body.appendChild(container);

      recaptchaVerifier = new firebase.auth.RecaptchaVerifier(containerId, {
        size: 'invisible',
        callback: function () { /* solved */ }
      });

      logStage('send_request');

      firebase.auth().signInWithPhoneNumber(phoneNumber, recaptchaVerifier)
        .then(function (result) {
          confirmationResult = result;
          logStage('code_sent');
          onCodeSent();
        })
        .catch(function (error) {
          var msg = error.message || error.code || 'SMS sending failed';
          logStage('verification_failed', error && error.code, error && error.message);
          if (error.code === 'auth/too-many-requests') {
            msg = 'Pārāk daudz mēģinājumu. Mēģiniet vēlāk.';
          }
          onError(msg);
        });
    },

    /**
     * Verify the OTP code and send the ID token to the backend.
     * @param {string} code - 6-digit OTP
     * @param {string} csrfName - CSRF token name
     * @param {string} csrfHash - CSRF token hash
     * @param {function} onSuccess - Called on success
     * @param {function} onError - Called on error with message string
     */
    verifyCode: function (code, csrfName, csrfHash, onSuccess, onError) {
      if (!confirmationResult) {
        onError('No verification in progress');
        return;
      }

      confirmationResult.confirm(code)
        .then(function (result) {
          // User typed a valid code — Firebase confirmed the credential.
          // (server_verify_attempt/success/failure are logged on the backend.)
          logStage('verification_completed');
          return result.user.getIdToken();
        })
        .then(function (idToken) {
          var formData = new FormData();
          formData.append('firebase_id_token', idToken);
          formData.append(csrfName, csrfHash);

          return fetch('/profile/verify-phone', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
        })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          // Sign out from Firebase — we only needed the token
          firebase.auth().signOut().catch(function () {});

          if (data.success) {
            if (data.csrf_hash) {
              window._latestCsrfHash = data.csrf_hash;
            }
            onSuccess(data);
          } else {
            if (data.csrf_hash) {
              window._latestCsrfHash = data.csrf_hash;
            }
            onError(data.message || 'Verification failed');
          }
        })
        .catch(function (error) {
          var msg = error.message || error.code || 'Verification failed';
          logStage('verification_failed', error && error.code, error && error.message);
          if (error.code === 'auth/invalid-verification-code') {
            msg = 'Nepareizs verifikācijas kods.';
          } else if (error.code === 'auth/code-expired') {
            msg = 'Kods beidzies. Lūdzu, pieprasiet jaunu.';
          }
          onError(msg);
        });
    },

    /**
     * Format phone number to E.164.
     */
    formatE164: function (phone) {
      phone = (phone || '').trim().replace(/[^\d+]/g, '');
      if (phone.charAt(0) === '+') return phone;
      if (phone.substring(0, 2) === '00') return '+' + phone.substring(2);
      if (phone.length === 8) return '+371' + phone;
      return '+' + phone;
    }
  };
})();
